<template>
  <div class="date-range-picker-container">
    <!-- Quick Select Row -->
    <v-row class="mb-0">
      <v-col cols="12">
        <v-select
          v-model="selectedPreset"
          dense
          :items="presetOptions"
          item-text="label"
          item-value="value"
          item-disabled="disabled"
          label="Presets"
          outlined
          hide-details
          class="preset-select"
          @change="applyPreset"
        >
          <template v-slot:prepend-inner>
            <font-awesome-icon
              :icon="['fas', 'bolt-lightning']"
              class="mr-2 mt-1 grey--text"
            />
          </template>

          <template v-slot:item="{ item }">
            <span :class="{ 'grey--text': item.disabled }">
              {{ item.label }}
            </span>
          </template>

          <template v-slot:append-inner>
            <font-awesome-icon
              :icon="['fas', 'chevron-down']"
              class="mr-2"
            />
          </template>
        </v-select>
      </v-col>
    </v-row>

    <!-- Custom Date Range Row -->
    <v-row class="my-0">
      <v-col cols="6">
        <v-menu
          v-model="startDateMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedStartDate"
              dense
              label="From Date"
              readonly
              outlined
              hide-details
              class="date-field"
              v-bind="attrs"
              v-on="on"
            >
              <template v-slot:prepend-inner>
                <font-awesome-icon
                  :icon="['fas', 'calendar-alt']"
                  class="mr-2 mt-1 grey--text"
                />
              </template>
            </v-text-field>
          </template>

          <v-date-picker
            v-model="startDate"
            :max="maxDate"
            @change="onManualDateChange('start'); startDateMenu = false"
          />
        </v-menu>
      </v-col>

      <v-col cols="6">
        <v-menu
          v-model="endDateMenu"
          :close-on-content-click="false"
          :nudge-right="40"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedEndDate"
              dense
              label="To Date"
              readonly
              outlined
              hide-details
              class="date-field"
              v-bind="attrs"
              v-on="on"
            >
              <template v-slot:prepend-inner>
                <font-awesome-icon
                  :icon="['fas', 'calendar-alt']"
                  class="mr-2 mt-1 grey--text"
                />
              </template>
            </v-text-field>
          </template>

          <v-date-picker
            v-model="endDate"
            :max="maxDate"
            :min="startDate"
            @change="onManualDateChange('end'); endDateMenu = false"
          />
        </v-menu>
      </v-col>
    </v-row>

    <!-- Action Buttons Row -->
    <v-row class="justify-space-between my-2">
      <v-col cols="auto">
        <v-btn
          class="tonal-clear-btn elevation-0"
          :disabled="!hasDateRange"
          @click="clearDateRange"
        >
          <font-awesome-icon
            :icon="['fas', 'times-circle']"
            size="sm"
            class="mr-2"
          />
          Clear
        </v-btn>
      </v-col>

      <v-spacer />

      <v-col cols="auto">
        <v-btn
          color="primary"
          :disabled="!isValidRange"
          :loading="loading"
          @click="applyDateRange"
        >
          <font-awesome-icon
            :icon="['fas', 'check']"
            size="sm"
            class="mr-2"
          />
          Apply
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import dates from 'src/utils/dates'

export default {
  name: 'DateRangePicker',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    maxDate: {
      type: String,
      default: () => dates.getFormattedDate(dates.now()),
    },
    initialStartDate: {
      type: String,
      default: null,
    },
    initialEndDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      startDate: this.initialStartDate || null,
      endDate: this.initialEndDate || null,
      startDateMenu: false,
      endDateMenu: false,
      selectedPreset: null,
      isApplyingPreset: false, // Flag to prevent clearing preset during programmatic changes
      presetOptions: [
        { label: 'Last 7 Days', value: 'last-7-days' },
        { label: 'Last 30 Days', value: 'last-30-days' },
        { label: 'Last 90 Days', value: 'last-90-days' },
        { label: 'This Week', value: 'this-week' },
        { label: 'Last Week', value: 'last-week' },
        { label: 'This Month', value: 'this-month' },
        { label: 'Last Month', value: 'last-month' },
        { label: 'Month to Date', value: 'mtd' },
        { label: 'Year to Date', value: 'ytd' },
        { label: 'Custom Date Range', value: 'custom', disabled: true },
      ],
    }
  },
  computed: {
    formattedStartDate() {
      return this.startDate ? dates.getFormattedDate(this.startDate, 'MMM DD, YYYY') : ''
    },
    formattedEndDate() {
      return this.endDate ? dates.getFormattedDate(this.endDate, 'MMM DD, YYYY') : ''
    },
    isValidRange() {
      if (!this.startDate || !this.endDate) return false
      return dates.get(this.startDate).isSameOrBefore(dates.get(this.endDate))
    },
    hasDateRange() {
      return this.startDate || this.endDate
    },

    // Automatically detect which preset matches the current date range
    detectedPreset() {
      if (!this.startDate || !this.endDate) {
        return null
      }

      const now = dates.now()

      // Helper function to get preset date range
      const getPresetRange = (presetValue) => {
        let presetStart
        let presetEnd

        switch (presetValue) {
          case 'this-week': {
            const week = dates.getWeek(0)
            const [monday, , , , , , sunday] = week
            presetStart = monday
            presetEnd = sunday
            break
          }
          case 'last-week': {
            const week = dates.getWeek(-1)
            const [monday, , , , , , sunday] = week
            presetStart = monday
            presetEnd = sunday
            break
          }
          case 'this-month': {
            presetStart = now.startOf('month')
            presetEnd = now.endOf('month')
            break
          }
          case 'last-month': {
            const lastMonth = now.subtract(1, 'month')
            presetStart = lastMonth.startOf('month')
            presetEnd = lastMonth.endOf('month')
            break
          }
          case 'mtd': {
            presetStart = now.startOf('month')
            presetEnd = now
            break
          }
          case 'ytd': {
            presetStart = now.startOf('year')
            presetEnd = now
            break
          }
          case 'last-7-days': {
            presetStart = now.subtract(6, 'days')
            presetEnd = now
            break
          }
          case 'last-30-days': {
            presetStart = now.subtract(29, 'days')
            presetEnd = now
            break
          }
          case 'last-90-days': {
            presetStart = now.subtract(89, 'days')
            presetEnd = now
            break
          }
          default:
            return null
        }

        return { presetStart, presetEnd }
      }

      // Find matching preset (excluding the custom option)
      const matchingPreset = this.presetOptions.find((preset) => {
        if (preset.value === 'custom') return false // Skip the custom option

        const range = getPresetRange(preset.value)
        if (!range || !range.presetStart || !range.presetEnd) {
          return false
        }

        const presetStartFormatted = dates.getFormattedDate(range.presetStart)
        const presetEndFormatted = dates.getFormattedDate(range.presetEnd)

        return presetStartFormatted === this.startDate && presetEndFormatted === this.endDate
      })

      // If no preset matches and we have both start and end dates, show "Custom Date Range"
      if (!matchingPreset && this.startDate && this.endDate) {
        return 'custom'
      }

      return matchingPreset ? matchingPreset.value : null
    },
  },
  watch: {
    initialStartDate(newVal) {
      this.startDate = newVal
    },
    initialEndDate(newVal) {
      this.endDate = newVal
    },
    detectedPreset: {
      handler(newPreset) {
        // Update selectedPreset to match the detected preset
        // This will happen when external changes occur (like tab switching)
        this.selectedPreset = newPreset
      },
      immediate: true,
    },
  },
  methods: {
    applyDateRange() {
      if (!this.isValidRange) return

      const dateRange = [
        dates.get(this.startDate),
        dates.get(this.endDate),
      ]

      this.$emit('apply-date-range', dateRange)
    },
    clearDateRange() {
      this.isApplyingPreset = true // Prevent unwanted preset detection during clear
      this.startDate = null
      this.endDate = null
      this.selectedPreset = null
      this.$emit('clear-date-range')

      this.$nextTick(() => {
        this.isApplyingPreset = false
      })
    },

    applyPreset(presetValue) {
      if (!presetValue) return

      // Handle custom preset selection - it's just a display option, don't change dates
      if (presetValue === 'custom') {
        // Keep the current dates as they are, just update the selected preset
        return
      }

      this.isApplyingPreset = true // Prevent clearing preset during this update
      const now = dates.now()
      let startDate
      let endDate

      switch (presetValue) {
        case 'this-week': {
          const week = dates.getWeek(0) // Current week
          const [monday, , , , , , sunday] = week
          startDate = monday
          endDate = sunday
          break
        }
        case 'last-week': {
          const week = dates.getWeek(-1) // Last week
          const [monday, , , , , , sunday] = week
          startDate = monday
          endDate = sunday
          break
        }
        case 'this-month': {
          startDate = now.startOf('month')
          endDate = now.endOf('month')
          break
        }
        case 'last-month': {
          const lastMonth = now.subtract(1, 'month')
          startDate = lastMonth.startOf('month')
          endDate = lastMonth.endOf('month')
          break
        }
        case 'mtd': {
          startDate = now.startOf('month')
          endDate = now
          break
        }
        case 'ytd': {
          startDate = now.startOf('year')
          endDate = now
          break
        }
        case 'last-7-days': {
          startDate = now.subtract(6, 'days')
          endDate = now
          break
        }
        case 'last-30-days': {
          startDate = now.subtract(29, 'days')
          endDate = now
          break
        }
        case 'last-90-days': {
          startDate = now.subtract(89, 'days')
          endDate = now
          break
        }
        default:
          return
      }

      // Update the date inputs
      this.startDate = dates.getFormattedDate(startDate)
      this.endDate = dates.getFormattedDate(endDate)

      // Automatically apply the preset
      this.applyDateRange()

      // Reset the flag after a brief delay to allow watchers to process
      this.$nextTick(() => {
        this.isApplyingPreset = false
      })
    },

    onManualDateChange(type) {
      // When user manually changes dates, the detectedPreset computed
      // will automatically update selectedPreset if the new dates match a preset
      // No need to clear selectedPreset here since it will be handled by the computed property

      // Optional: Could emit an event here if parent needs to know about manual changes
      this.$emit('manual-date-change', { type, startDate: this.startDate, endDate: this.endDate })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.date-range-picker-container {
  min-width: 360px;
  padding: 0;

  .preset-select {
    // Style for custom date range option
    ::v-deep .v-select__selection--comma {
      color: var(--v-primary-base) !important;
      font-style: italic;
      font-weight: 600;
    }

    // Subtle icon styling
    ::v-deep .v-input__icon--prepend-inner {
      margin-top: 8px;
      margin-right: 8px;
      color: #b0b3b8 !important; // Lighter gray
    }
  }

  .date-field {
    // Subtle icon styling for date fields
    ::v-deep .v-input__icon--prepend-inner {
      margin-top: 8px;
      margin-right: 8px;
      color: #b0b3b8 !important; // Lighter gray
    }

    // Reduce input height for a more compact look
    ::v-deep .v-input__control {
      min-height: 38px;
      height: 38px !important;
    }

    // Improve text styling
    ::v-deep .v-text-field__slot input {
      font-weight: 500;
    }
  }

  // Action buttons row styling
  .v-row:last-child {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);

    .v-btn {
      font-weight: 600;
      letter-spacing: 0.5px;

      &:not(.v-btn--text) {
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

        &:hover {
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        }
      }
    }
  }
}

// Dark mode support
.theme--dark .date-range-picker-container {
  .v-row:last-child {
    border-top-color: rgba(255, 255, 255, 0.12);
  }
}

// Responsive adjustments
@media (max-width: 480px) {
  .date-range-picker-container {
    min-width: 280px;

    .date-field {
      ::v-deep .v-input__control {
        min-height: 44px;
      }
    }
  }
}
</style>
/* Tonal style for Clear button (Vue 2 mimic of Vue 3 tonal) */
.tonal-clear-btn {
  background-color: rgba(226, 115, 98, 0.18); /* hydrogen toasted, semi-transparent */
  color: #e27362;
  box-shadow: 0 1px 4px rgba(226, 115, 98, 0.10);
  border-radius: 6px;
  font-weight: 500;
  transition: background 0.2s, box-shadow 0.2s;
}
.tonal-clear-btn:hover,
.tonal-clear-btn:focus {
  background-color: rgba(226, 115, 98, 0.28);
  color: #d16454;
  box-shadow: 0 2px 8px rgba(226, 115, 98, 0.18);
}
